/**
 * Repeatable processor — manages batch-create mode with useFieldArray.
 * When mode.repeatable is configured, the entire form layout is rendered N times
 * as rows within a `items` field array.
 */

import { useCallback } from 'react';
import { useFieldArray, type UseFormReturn, type UseFieldArrayReturn } from 'react-hook-form';
import type { FormMode, RepeatableAddButtonConfig, RepeatableCardConfig } from '../types/schema';
import type { FormLayoutNode } from '../types/layout';

export interface RepeatableProcessorConfig {
  form: UseFormReturn<Record<string, unknown>>;
  mode: FormMode;
  layout: FormLayoutNode[];
}

export interface RepeatableProcessorReturn {
  fields: UseFieldArrayReturn['fields'];
  append: () => void;
  remove: (index: number) => void;
  canAdd: boolean;
  canRemove: boolean;
  addLabel: string;
  addButton: RepeatableAddButtonConfig;
  cardConfig: RepeatableCardConfig;
  minItems: number;
  maxItems: number;
}

export function useRepeatableMode({
  form,
  mode,
  layout,
}: RepeatableProcessorConfig): RepeatableProcessorReturn | null {
  const rep = mode.repeatable;
  const isRepeatable = Boolean(rep);
  const minItems = rep?.minItems ?? 1;
  const maxItems = rep?.maxItems ?? Infinity;
  const addLabel = rep?.addLabel ?? 'Add Row';
  const addButton: RepeatableAddButtonConfig = rep?.addButton ?? {};
  const cardConfig: RepeatableCardConfig = rep?.card ?? {};

  const { fields, append: fieldArrayAppend, remove: fieldArrayRemove } = useFieldArray({
    control: form.control as any,
    name: 'items',
  });

  const append = useCallback(() => {
    if (fields.length < maxItems) {
      fieldArrayAppend({});
    }
  }, [fields.length, maxItems, fieldArrayAppend]);

  const remove = useCallback((index: number) => {
    if (fields.length > minItems) {
      fieldArrayRemove(index);
    }
  }, [fields.length, minItems, fieldArrayRemove]);

  const canAdd = fields.length < maxItems;
  const canRemove = fields.length > minItems;

  if (!isRepeatable) return null;

  return {
    fields,
    append,
    remove,
    canAdd,
    canRemove,
    addLabel,
    addButton,
    cardConfig,
    minItems,
    maxItems,
  };
}
